/**
 * Voice capability detection.
 *
 * `detectVoiceCapability()` — one-shot feature check.
 * `subscribePermissionChange(cb)` — live mic permission observer.
 */

import type { VoiceCapability } from '@/voice/types';

/**
 * Returns 'supported' when the browser has `navigator.mediaDevices.getUserMedia`,
 * 'unsupported' otherwise (SSR, old browser, secure-context missing, etc.).
 */
export function detectVoiceCapability(): VoiceCapability {
  if (
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices != null &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  ) {
    return 'supported';
  }
  return 'unsupported';
}

type PermissionState = 'granted' | 'denied' | 'prompt';

/**
 * Subscribes to mic permission changes via the Permissions API.
 *
 * - Calls `cb` immediately with the current state (if the API is available).
 * - Calls `cb` on every subsequent `change` event.
 * - Returns a cleanup function that removes the listener.
 *
 * If `navigator.permissions` is unavailable, returns a no-op and never calls `cb`.
 */
export function subscribePermissionChange(
  cb: (state: PermissionState) => void,
): () => void {
  if (
    typeof navigator === 'undefined' ||
    navigator.permissions == null ||
    typeof navigator.permissions.query !== 'function'
  ) {
    return () => {
      // no-op — permissions API not available
    };
  }

  let permStatus: PermissionStatus | null = null;

  const onChange = () => {
    if (permStatus !== null) {
      cb(permStatus.state as PermissionState);
    }
  };

  navigator.permissions.query({ name: 'microphone' as PermissionName }).then((status) => {
    permStatus = status;
    cb(status.state as PermissionState);
    status.addEventListener('change', onChange);
  }).catch(() => {
    // Permissions API present but query failed (e.g. Firefox private mode)
  });

  return () => {
    if (permStatus !== null) {
      permStatus.removeEventListener('change', onChange);
    }
  };
}
